import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    startConnection,
    stopConnection,
    listenToSocket,
    stopListenToSocket,
} from "@icarius-connection/webSocketHandlers";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import { getLocalizedString } from "@icarius-localization/strings";
import paths from "@icarius-router/paths";
import { getVoiceGenreAction } from "@icarius-common/audioPlayer/actions";
import { getSocietySelected } from "@icarius-pages/login/selectors";
import { killTokens, logOutAction } from "@icarius-pages/login/actions";
import { getAppColor, getIsLoading, getUserRoles, getToken } from "@icarius-app/selectors";
import { exportTableAction, getInitialDataAction } from "@icarius-app/actions";
import { setExportToExcelFunction, setSocietyName } from "@icarius-table/utils";
import useVisitPage from "@icarius-utils/hooks/useVisitPage";
import useGetRoutes from "./useGetRoutes";
import useDocumentTitle from "./useDocumentTitle";
import useRedirectPathNotFound from "./useRedirectPathNotFound";
import useDrawerDialogsState from "./useDrawerDialogsState";
import useChangeSociety from "./useChangeSociety";

const useWrapper = () => {
    const [drawerIsOpen, setDrawerIsOpen] = useState(false);

    const { availablePaths, allRoutes, customRoutes } = useGetRoutes();

    const changeSocietyData = useChangeSociety();

    const dispatch = useDispatch();
    const isLoading = useSelector(getIsLoading);
    const token = useSelector(getToken);
    const history = useHistory();
    const dialogsState = useDrawerDialogsState();
    const color = useSelector(getAppColor);
    const societySelected = useSelector(getSocietySelected);
    const userRoles = useSelector(getUserRoles);

    useEffect(() => {
        dispatch(getInitialDataAction()).then((resp) => {
            if (resp.status === 200) {
                dispatch(getVoiceGenreAction());
            }
        });

        // seteos del agGrid
        setExportToExcelFunction((...args) => dispatch(exportTableAction(...args)));
        setSocietyName(societySelected.name);
    }, [dispatch, societySelected.name]);

    // socket connection
    useEffect(() => {
        if (!token) return;
        startConnection(token, dispatch);
        listenToSocket(dispatch);

        return () => {
            stopListenToSocket();
            stopConnection();
        };
    }, [dispatch, token]);

    useEffect(() => {
        const handleOffline = () => {
            dispatch(
                openSnackbarAction({
                    msg: getLocalizedString("noConnection"),
                    severity: "error",
                    duration: null,
                })
            );
        };

        window.addEventListener("offline", handleOffline);

        return () => {
            window.removeEventListener("offline", handleOffline);
        };
    }, [dispatch]);

    useEffect(() => {
        document.documentElement.style.setProperty("--corporate-color", color); // setear variable de css con el color corporativo (para usarla en css de grillas)
    }, [color]);

    useVisitPage();
    useDocumentTitle();
    useRedirectPathNotFound(allRoutes);

    const handleOpenItem = (item) => {
        switch (item?.path) {
            case paths.myReceipts: {
                dialogsState.setMyReceiptsDialogIsOpen(true);
                break;
            }
            case paths.myHistoricalReceipts: {
                dialogsState.setMyHistoricalReceiptsDialogIsOpen(true);
                break;
            }
            case paths.myCertificates: {
                dialogsState.setMyCertificatesDialogIsOpen(true);
                break;
            }
            case paths.myVacations: {
                dialogsState.setMyVacationsDialogIsOpen(true);
                break;
            }
            case paths.requestDocuments: {
                dialogsState.setRequestDocumentsDialogIsOpen(true);
                break;
            }
            case paths.digitalDocuments: {
                dialogsState.setDigitalDocumentsIsOpen(true);
                break;
            }
            case paths.myPIN: {
                dialogsState.setChangePinDialogIsOpen(true);
                break;
            }
            case paths.changeSociety: {
                changeSocietyData.handleOpenChangeSocietyDialog();
                break;
            }
            case paths.changeRole: {
                dialogsState.setChangeRoleDialogIsOpen(true);
                break;
            }
            case paths.aboutUs: {
                dialogsState.setAboutUsDialogIsOpen(true);
                break;
            }
            case paths.logOut: {
                dispatch(logOutAction);
                dispatch(killTokens(token));
                history.push("");
                break;
            }
            default: {
                history.push({
                    pathname: item.path,
                    search: item.search,
                });
                break;
            }
        }
    };

    const canChangeRole = userRoles?.length > 1;

    return {
        isLoading,
        color,
        availablePaths,
        allRoutes,
        customRoutes,
        canChangeRole,
        dialogsState,
        changeSocietyData,
        drawerIsOpen,
        setDrawerIsOpen,
        handleOpenItem,
    };
};

export default useWrapper;
